import React from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../../components/layout/PageHeader";
import {
  Button,
  EmptyStateProps,
  failedEmptyState,
  useLoadPhase,
} from "../../../components/ui";
import { userDetailPath } from "../../../routes/paths";
import { useAdmin } from "../../auth";
import ActivityCard from "../components/ActivityCard";
import SignupActivityTable from "../components/SignupActivityTable";
import StatTile from "../components/StatTile";
import SubscriptionActivityTable from "../components/SubscriptionActivityTable";
import WelcomeCard from "../components/WelcomeCard";
import useDashboard from "../hooks/useDashboard";

/** What a card shows in place of a figure that never arrived. */
const NO_FIGURE = "—";

/**
 * Where the panel opens: how the month is going, how many accounts there are,
 * and what has happened on them lately.
 *
 * Three bands. The greeting and the three counts beside it are the state of the
 * business at a glance; under them, the last five payments and the last five
 * arrivals. Nothing on this screen acts on an account — a row opens that
 * account's own screen, and anything that changes one lives there.
 *
 * ## What is deliberately not here yet
 *
 * The design has a second band between the counts and the feeds — a revenue and
 * subscriptions plot, the average renewal rate, and a status breakdown pie. It
 * is not built. Two of the three now have their figures: the dashboard route
 * sends the renewal rate and the account split, and `useDashboard` carries both
 * through on `summary` for whoever draws them. The plot still wants a monthly
 * series nobody is sending, and a chart drawn from numbers that do not exist is
 * a picture of nothing. The band goes in between the grid above and the first
 * feed below, whenever it is built.
 *
 * ## How a failure reads
 *
 * The toast says it happened and why — raised in `useDashboard`, gone a moment
 * later. What stays on the screen is the absence itself: a dash where every
 * figure would be, and both feeds carrying the retry. There is deliberately no
 * banner across the top any more. It pushed the whole page down as it arrived,
 * outstayed the moment it was reporting, and left the figures underneath it
 * printing zeroes — and a zero is a fact about the business, not about the
 * network. A screen that cannot say how many accounts there are should say
 * nothing rather than say none.
 */

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { admin } = useAdmin();
  const { data, status, reload } = useDashboard();

  const phase = useLoadPhase(status === "loading");
  const summary = data?.summary;

  // The name is nice to have and never worth waiting for — the greeting reads
  // fine without it and fills in when the profile lands.
  const firstName = admin?.name?.split(" ")[0];

  const subscriptions = data?.recentSubscriptions ?? [];
  const signups = data?.recentSignups ?? [];

  // `?? NO_FIGURE` rather than `?? 0`: a count that is genuinely zero is still
  // a number and keeps its tile, and only a summary that never arrived falls
  // through to the dash.
  const tiles: { label: string; value: number | string; emptyMessage?: string }[] =
    [
      {
        label: "Total accounts",
        value: summary?.totalAccounts ?? NO_FIGURE,
        emptyMessage: "No accounts yet",
      },
      { label: "Active trial", value: summary?.activeTrial ?? NO_FIGURE },
      { label: "Pro accounts", value: summary?.proAccounts ?? NO_FIGURE },
    ];

  /**
   * What a feed shows in place of rows.
   *
   * Two cases, and they are not the same thing: a call that failed, and a
   * console with nothing in it yet. The first is where the retry lives now
   * that the banner is gone — one per feed, because a reader who has scrolled
   * to the second one should not have to scroll back up to try again.
   */
  const feedEmptyState = (
    failedTitle: string,
    empty: EmptyStateProps
  ): EmptyStateProps =>
    status === "failed"
      ? failedEmptyState(
          failedTitle,
          <Button size="sm" variant="secondary" onClick={reload}>
            Try again
          </Button>
        )
      : empty;

  return (
    <div className="min-h-full px-4 py-5 sm:px-6 lg:px-8 3xl:px-12 3xl:py-8">
      <PageHeader title="Dashboard" />

      {status === "loading" && (
        <p role="status" className="sr-only">
          Loading dashboard…
        </p>
      )}

      {/* Six columns so the greeting takes half the band and the three counts
          share the other half. At `sm` the card takes a row of its own and the
          counts sit three across under it; below that everything stacks. */}
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-4 3xl:gap-6">
        <WelcomeCard
          name={firstName || "Admin"}
          summary={summary}
          phase={phase}
          className="sm:col-span-3"
        />

        {tiles.map((tile) => (
          <StatTile
            key={tile.label}
            label={tile.label}
            value={tile.value}
            // The empty line only stands in for a genuine nothing. A figure
            // that is simply small is still a figure.
            emptyMessage={tile.value === 0 ? tile.emptyMessage ?? null : null}
            phase={phase}
          />
        ))}
      </div>

      <div className="mt-4 3xl:mt-6 flex flex-col gap-4 3xl:gap-6">
        <ActivityCard
          title="Recent subscription activity"
          phase={phase}
          hasRows={subscriptions.length > 0}
        >
          <SubscriptionActivityTable
            subscriptions={subscriptions}
            empty={feedEmptyState("We could not load subscription activity", {
              title: "No subscription activity yet",
              description:
                "Payments, renewals and abandoned checkouts will appear here as they happen.",
            })}
            onSelect={(subscription) =>
              navigate(userDetailPath(subscription.userId))
            }
          />
        </ActivityCard>

        <ActivityCard
          title="Recent signup activity"
          phase={phase}
          hasRows={signups.length > 0}
        >
          <SignupActivityTable
            signups={signups}
            empty={feedEmptyState("We could not load signup activity", {
              title: "No signups yet",
              description:
                "New Naurist accounts will appear here as soon as people sign up.",
            })}
            onSelect={(signup) => navigate(userDetailPath(signup.userId))}
          />
        </ActivityCard>
      </div>
    </div>
  );
};

export default DashboardPage;
